/**
 * Scales and ticks: the arithmetic that turns data values into SVG coordinates.
 *
 * Pure functions, no DOM. Every chart and diagram widget lays itself out with these
 * during render, which is what lets a chart be server-rendered from its example data and
 * then re-drawn on each keystroke without measuring anything.
 *
 * The tick algorithm is the one d3 and R's `pretty()` share: a step of 1, 2 or 5 × 10ᵏ
 * close to (span ÷ requested count), and a domain widened outward to whole steps. Ticks
 * are generated as integer multiples of the step and rounded to the step's own decimals,
 * so an axis never shows 0.30000000000000004.
 */

/** Maps a data value to a coordinate. */
export type Scale = (value: number) => number;

/**
 * A linear map from `domain` to `range`. A zero-width domain maps everything to the
 * middle of the range rather than dividing by zero.
 */
export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number]
): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0 || !Number.isFinite(span)) {
    const middle = (r0 + r1) / 2;
    return () => middle;
  }
  const ratio = (r1 - r0) / span;
  return (value) => r0 + (value - d0) * ratio;
}

/** The smallest and largest finite value, or null when there is none. */
export function extent(
  values: Iterable<number | null | undefined>
): [number, number] | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      continue;
    }
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }
  return min <= max ? [min, max] : null;
}

/**
 * How many decimals a step needs to be written exactly: 5 → 0, 0.5 → 1, 0.25 → 2.
 * Used to give every tick label on an axis the same number of decimals.
 */
export function stepDecimals(step: number): number {
  if (!Number.isFinite(step) || step <= 0) {
    return 0;
  }
  for (let decimals = 0; decimals <= 12; decimals++) {
    const scaled = step * 10 ** decimals;
    if (Math.abs(scaled - Math.round(scaled)) < 1e-9 * Math.max(1, scaled)) {
      return decimals;
    }
  }
  return 12;
}

/** Round to a number of decimals without binary noise (0.1 + 0.2 → 0.3). */
export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return value;
  }
  return Number(value.toFixed(Math.min(20, Math.max(0, decimals))));
}

const E10 = Math.sqrt(50);
const E5 = Math.sqrt(10);
const E2 = Math.sqrt(2);

/** A 1–2–5 step close to `span / count`. */
export function niceStep(span: number, count: number): number {
  const raw = span / Math.max(1, count);
  if (!(raw > 0 && Number.isFinite(raw))) {
    return 1;
  }
  const power = Math.floor(Math.log10(raw));
  const error = raw / 10 ** power;
  let factor = 1;
  if (error >= E10) {
    factor = 10;
  } else if (error >= E5) {
    factor = 5;
  } else if (error >= E2) {
    factor = 2;
  }
  return roundTo(factor * 10 ** power, Math.max(0, -power));
}

export interface NiceTicks {
  /** The domain, widened outward to whole steps (unless `exact`). */
  min: number;
  max: number;
  step: number;
  ticks: number[];
  /** Decimals every tick label should show. */
  decimals: number;
}

export interface NiceTickOptions {
  /** Roughly how many intervals to aim for. Default 5. */
  count?: number;
  /** Widen the domain to include zero — bar charts must start at 0. */
  includeZero?: boolean;
  /**
   * Keep `min`/`max` as given and only place ticks inside them, instead of widening
   * the domain to whole steps. For a user-chosen axis range, or numeric x values.
   */
  exact?: boolean;
  /** Counts: never a step below 1, so the axis cannot show half a person. */
  integer?: boolean;
}

/** A sensible non-empty interval around a single value. */
function widenDegenerate(
  value: number,
  includeZero: boolean
): [number, number] {
  if (value === 0) {
    return [0, 1];
  }
  if (includeZero) {
    return value > 0 ? [0, value] : [value, 0];
  }
  const pad = Math.abs(value) * 0.1;
  return [value - pad, value + pad];
}

function ticksBetween(min: number, max: number, step: number): number[] {
  const decimals = stepDecimals(step);
  const first = Math.ceil(min / step - 1e-9);
  const last = Math.floor(max / step + 1e-9);
  const ticks: number[] = [];
  for (let i = first; i <= last && ticks.length <= 1000; i++) {
    // `|| 0` turns −0 into 0 so an axis never prints "−0".
    ticks.push(roundTo(i * step, decimals) || 0);
  }
  return ticks;
}

/**
 * Ticks for a value axis.
 *
 * Without `exact`, the domain is widened to the nearest whole steps, repeating once if
 * widening changed the step (the d3 `nice()` loop), so the outermost ticks sit on the
 * ends of the axis.
 */
export function niceTicks(
  rawMin: number,
  rawMax: number,
  {
    count = 5,
    includeZero = false,
    exact = false,
    integer = false,
  }: NiceTickOptions = {}
): NiceTicks {
  let min = Math.min(rawMin, rawMax);
  let max = Math.max(rawMin, rawMax);
  if (!(Number.isFinite(min) && Number.isFinite(max))) {
    [min, max] = [0, 1];
  }
  if (includeZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  if (min === max) {
    [min, max] = widenDegenerate(min, includeZero);
  }
  const stepFor = (span: number) =>
    integer ? Math.max(1, niceStep(span, count)) : niceStep(span, count);
  let step = stepFor(max - min);
  if (!exact) {
    for (let pass = 0; pass < 4; pass++) {
      const decimals = stepDecimals(step);
      const lower = roundTo(Math.floor(min / step + 1e-9) * step, decimals);
      const upper = roundTo(Math.ceil(max / step - 1e-9) * step, decimals);
      const next = stepFor(upper - lower);
      min = lower;
      max = upper;
      if (next === step) {
        break;
      }
      step = next;
    }
  }
  return {
    min,
    max,
    step,
    ticks: ticksBetween(min, max, step),
    decimals: stepDecimals(step),
  };
}

/** Positions for a row of equal slots — the categories of a bar chart. */
export interface BandScale {
  /** Distance from one band's start to the next. */
  step: number;
  /** Width of one band (the bar's slot). */
  bandwidth: number;
  /** Start coordinate of band `index`. */
  start: (index: number) => number;
  /** Centre coordinate of band `index`. */
  center: (index: number) => number;
}

export interface BandScaleOptions {
  /** Fraction of a step left empty between bands. Default 0.2. */
  paddingInner?: number;
  /** Fraction of a step left empty before the first and after the last band. */
  paddingOuter?: number;
}

export function bandScale(
  count: number,
  range: readonly [number, number],
  { paddingInner = 0.2, paddingOuter = 0.1 }: BandScaleOptions = {}
): BandScale {
  const [r0, r1] = range;
  const slots = Math.max(1, count) - paddingInner + 2 * paddingOuter;
  const step = (r1 - r0) / Math.max(slots, 1e-9);
  const bandwidth = step * (1 - paddingInner);
  const first = r0 + step * paddingOuter;
  return {
    step,
    bandwidth,
    start: (index) => first + index * step,
    center: (index) => first + index * step + bandwidth / 2,
  };
}

/**
 * The nearest "convenient" width to `raw` on a log scale, from 1, 2, 2.5 and 5 × 10ᵏ —
 * the widths a textbook rounds a class width to (4.57 → 5, 0.118 → 0.1, 3.4 → 2.5).
 */
export function nearestNiceWidth(raw: number): number {
  if (!(raw > 0 && Number.isFinite(raw))) {
    return 1;
  }
  const power = Math.floor(Math.log10(raw));
  let best = 10 ** power;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const exponent of [power, power + 1]) {
    for (const mantissa of [1, 2, 2.5, 5]) {
      const candidate = roundTo(
        mantissa * 10 ** exponent,
        Math.max(0, 1 - exponent)
      );
      const distance = Math.abs(Math.log(candidate / raw));
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
  }
  return best;
}

export interface AxisRange extends NiceTicks {
  /** True when the reader's bounds were unusable (start not below end) and ignored. */
  bad: boolean;
}

/**
 * An axis over `values` where the reader may pin either end. The automatic range is
 * the data's nice range — not forced to zero, since a scatter plot's axes need not
 * start there. A pinned end is used exactly, with ticks placed inside the range.
 */
export function boundedRange(
  values: readonly number[],
  low: number | null,
  high: number | null
): AxisRange {
  const [dataMin, dataMax] = extent(values) ?? [0, 1];
  const auto = niceTicks(dataMin, dataMax);
  if (low === null && high === null) {
    return { ...auto, bad: false };
  }
  const min = low ?? auto.min;
  const max = high ?? auto.max;
  if (!(min < max)) {
    return { ...auto, bad: true };
  }
  return { ...niceTicks(min, max, { exact: true }), bad: false };
}
